import React, { useState, useEffect, useRef } from "react";
import ResultCard from "../cards/ResultCard";
import CustomYouTubePlayer from "../learning/CustomYouTubePlayer";
import { getCourseDetails, getCourseVideos, unlockFreeResource } from "../../api/contentApi";
import { getLearningDashboard } from "../../api/learningApi";
import { createPhonePePayment } from "../../api/ordersApi";
import customCertificateImg from "../../assets/certificate.webp";
import "../../styles/ApiContentState.css";
import "../../styles/VideoEditingCourse.css";
import "../../styles/RealResults.css";

function parseCourseFeatures(value) {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
  }

  const text = String(value).trim();
  if (!text) return [];

  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
    }
  } catch {
    // Store plain text values as delimiter-separated features.
  }

  return text
    .split(/[\r\n,|]+/)
    .map((item) => item.replace(/^[-*•✓✔]+\s*/, "").trim())
    .filter(Boolean);
}

function formatRemainingDuration(minutes) {
  const totalMinutes = Math.ceil(minutes);
  const hours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (hours && remainingMinutes) return `${hours}h ${remainingMinutes}m`;
  if (hours) return `${hours}h`;
  return `${remainingMinutes}m`;
}

export default function VideoEditingCourse({
  courseId = "premiere-pro-mastery",
  courseByTitle = false,
  currentUser,
}) {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedModules, setExpandedModules] = useState({ 1: true, 2: true });
  const [openFaq, setOpenFaq] = useState(null);
  const [activePlayingId, setActivePlayingId] = useState(null);
  const [databaseCourse, setDatabaseCourse] = useState(null);
  const heroTrailerRef = useRef(null);
  const [isHeroTrailerVisible, setIsHeroTrailerVisible] = useState(true);
  const [courseVideos, setCourseVideos] = useState([]);
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState("idle");
  const [purchaseError, setPurchaseError] = useState("");
  const [courseAccess, setCourseAccess] = useState({
    status: localStorage.getItem("cd_user_token") ? "checking" : "available",
    remainingMinutes: 0,
  });
  const [accessCheckVersion, setAccessCheckVersion] = useState(0);

  useEffect(() => {
    const updateTrailerVisibility = () => {
      const trailer = heroTrailerRef.current;
      if (!trailer) return;

      const bounds = trailer.getBoundingClientRect();
      const isVisible = bounds.bottom > 0 && bounds.top < window.innerHeight;
      setIsHeroTrailerVisible((previous) => previous === isVisible ? previous : isVisible);
    };

    updateTrailerVisibility();
    window.addEventListener("scroll", updateTrailerVisibility, { passive: true });
    window.addEventListener("resize", updateTrailerVisibility);

    return () => {
      window.removeEventListener("scroll", updateTrailerVisibility);
      window.removeEventListener("resize", updateTrailerVisibility);
    };
  }, [courseId, courseByTitle]);

     const handleOpenBonusModal = async (bonus) => {
       const token = localStorage.getItem("cd_user_token");
   
       // Login nahi hai
       if (!token) {
         window.location.hash = "#login";
         return;
       }
   
       try {
         await unlockFreeResource(bonus.id);

         const downloadUrls = [...new Set(
           bonusItems
             .map((item) => item.downloadUrl || item.link)
             .filter((url) => typeof url === "string" && url.trim()),
         )];

         if (!downloadUrls.length) {
           alert("Download links are not available for these resources yet.");
           return;
         }

         downloadUrls.forEach((url, index) => {
           window.setTimeout(() => {
             const link = document.createElement("a");
             link.href = url;
             link.download = "";
             document.body.appendChild(link);
             link.click();
             link.remove();
           }, index * 300);
         });
       } catch (error) {
         if (error.status === 401) {
           localStorage.removeItem("cd_user_token");
           localStorage.removeItem("cd_auth_user");
           window.location.hash = "#login";
           return;
         }
   
         alert(error.message || "Unable to download resource.");
       }
     };

  const handleCloseBonusModal = () => {
    setActiveBonusModal(null);
    setIsUnlocked(false);
  };

  const handleBonusFormSubmit = (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email || !leadForm.phone) {
      alert("Please enter your name, email, and phone number.");
      return;
    }
    setIsUnlocked(true);
  };

  const [activeBonusModal, setActiveBonusModal] = useState(null);
    const [isUnlocked, setIsUnlocked] = useState(false);
    const [leadForm, setLeadForm] = useState({ name: "", email: "", phone: "" });
  
    

  useEffect(() => {
    const lookup = String(courseId || '').trim();
    if (!lookup) {
      setDatabaseCourse(null);
      setCourseVideos([]);
      return undefined;
    }

    let cancelled = false;

    getCourseDetails(lookup, courseByTitle)
      .then((response) => {
        if (cancelled) return;

        if (!response?.status || !response.course) {
          window.location.hash = '#courses';
          setDatabaseCourse(null);
          return;
        }

        setDatabaseCourse(response.course);
      })
      .catch(() => {
        if (!cancelled) {
          window.location.hash = '#courses';
          setDatabaseCourse(null);
        }
      });

    getCourseVideos(lookup, courseByTitle)
      .then((response) => {
        if (!cancelled) {
          const videos = Array.isArray(response?.videos) ? response.videos : [];
          setCourseVideos(videos);
        }
      })
      .catch(() => {
        if (!cancelled) setCourseVideos([]);
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, courseByTitle]);

  useEffect(() => {
    if (!localStorage.getItem("cd_user_token")) {
      setCourseAccess({ status: "available", remainingMinutes: 0 });
      return undefined;
    }

    let cancelled = false;
    setCourseAccess({ status: "checking", remainingMinutes: 0 });

    getLearningDashboard()
      .then((response) => {
        if (cancelled) return;

        const resolvedCourseId = databaseCourse?.id;
        const activeCourse = resolvedCourseId
          ? (response?.courses || []).find(
              (course) => String(course.id) === String(resolvedCourseId),
            )
          : null;
        if (!activeCourse) {
          setCourseAccess({ status: "available", remainingMinutes: 0 });
          return;
        }

        const remainingMinutes = (activeCourse.lectures || []).reduce(
          (total, lecture) => total + (
            lecture.isCompleted ? 0 : Math.max(0, Number(lecture.duration) || 0)
          ),
          0,
        );
        setCourseAccess({ status: "enrolled", remainingMinutes });
      })
      .catch((error) => {
        console.error("Unable to verify course enrollment:", error);
        if (!cancelled) {
          if (error.status === 401) {
            localStorage.removeItem("cd_user_token");
            localStorage.removeItem("cd_auth_user");
            setCourseAccess({ status: "available", remainingMinutes: 0 });
          } else {
            setCourseAccess({ status: "error", remainingMinutes: 0 });
          }
        }
      });

    return () => {
      cancelled = true;
    };
  }, [courseId, currentUser, accessCheckVersion, databaseCourse?.id]);

  const courseTitle = databaseCourse?.title || "";
  const courseDescription = databaseCourse?.about || databaseCourse?.details || "";
  const coursePrice = Number(databaseCourse?.totalPrice ?? databaseCourse?.price ?? 0);
  const courseOriginalPrice = databaseCourse?.price ? `₹${databaseCourse.price}` : "₹0";
  const displayCoursePrice = coursePrice > 0 ? `₹${coursePrice.toLocaleString("en-IN")}` : "₹0";
  const displayCourseOriginalPrice = courseOriginalPrice && courseOriginalPrice !== "₹0" ? courseOriginalPrice : "₹0";
  const courseDuration = databaseCourse?.duration ? `${databaseCourse.duration}h` : "";
  const courseModuleCount = databaseCourse?.modules?.length || 0;
  const courseLectureCount = databaseCourse?.modules?.reduce(
    (total, module) => total + (module.lectures || []).length,
    0,
  ) || 0;
  const includedCourseFeatures = parseCourseFeatures(
    databaseCourse?.includedFeatures || databaseCourse?.features,
  );

  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    days: 2,
    hours: 14,
    minutes: 36,
    seconds: 28,
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: 59, seconds: 59 };
        } else if (prev.hours > 0) {
          return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else if (prev.days > 0) {
          return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        }
        return prev;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const [activeGalleryVideo, setActiveGalleryVideo] = useState(null);
  const topCreators = courseVideos.length
    ? courseVideos.slice(0, 3).map((video, index) => ({
        id: video.id ?? `video-${index}`,
        name: String(video.title || `Course Video ${index + 1}`).toUpperCase(),
        image: video.image || video.thumbnail || video.videoUrl || "https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?auto=format&fit=crop&w=600&q=80",
      }))
    : [];

  const galleryProjects = databaseCourse?.projects?.length
    ? databaseCourse.projects.map((project) => ({
        id: project.id,
        title: project.title,
        category: project.category || "Course Project",
        duration: project.duration ? `${project.duration}m` : "Watch",
        views: project.metaTitle || "Course project",
        thumbnail: project.imageUrl,
        videoUrl: project.videoUrl,
      }))
    : databaseCourse?.videos?.length
      ? databaseCourse.videos.map((video) => ({
          id: video.id,
          title: video.title,
          category: "Course Video",
          duration: "Watch",
          views: "Course lesson",
          thumbnail: video.thumbnail,
          videoUrl: video.videoUrl,
        }))
      : [];
  const bonusItems = Array.isArray(databaseCourse?.bonuses)
    ? databaseCourse.bonuses
    : Array.isArray(databaseCourse?.bonuses?.items)
      ? databaseCourse.bonuses.items
      : [];
  const bonusValue = databaseCourse?.bonusValue || databaseCourse?.bonuses?.subtitle || "";
  const editingCurriculum = databaseCourse
    ? (databaseCourse.modules || []).map((module) => ({
        id: module.id,
        title: module.title,
        meta: `${module.lectureCount || module.lectures?.length || 0} Lectures`,
        lectures: (module.lectures || []).map((lecture) => ({
          title: lecture.title,
          time: lecture.duration ? `${lecture.duration}m` : "",
        })),
      }))
    : [];
  const studentFeedbacks = databaseCourse
    ? (databaseCourse.reviews || []).slice(0, 3).map((review) => ({
        id: review.id,
        name: review.name,
        niche: "Course learner",
        startMetric: `${review.rating || 0}/5 rating`,
        growthMetric: review.review,
        videoThumbnail: review.imageUrl,
        avatarUrl: review.imageUrl,
      }))
    : [];
  const faqsList = (databaseCourse?.faqs || []).filter(
    (faq) => faq?.question && faq?.answer,
  );

  const toggleModule = (id) => {
    setExpandedModules((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleFaq = (idx) => {
    setOpenFaq((prev) => (prev === idx ? null : idx));
  };

  const handleEnrollClick = async () => {
    if (!localStorage.getItem("cd_user_token")) {
      window.location.hash = "#login";
      return;
    }
    if (courseAccess.status !== "available") return;

    setPurchaseStatus("idle");
    setPurchaseError("");
    setPurchaseDialogOpen(true);
    setPurchaseStatus("processing");

    try {
      const response = await createPhonePePayment(databaseCourse?.id || courseId);
      window.location.assign(response.paymentUrl);
    } catch (error) {
      if (error.status === 401) {
        localStorage.removeItem("cd_user_token");
        localStorage.removeItem("cd_auth_user");
        setPurchaseDialogOpen(false);
        window.location.hash = "#login";
        return;
      }
      setPurchaseError(error.message || "Unable to purchase this course.");
      setPurchaseStatus("error");
    }
  };

  const renderEnrollmentAction = (className, label) => {
    if (!localStorage.getItem("cd_user_token")) {
      return <button className={className} onClick={handleEnrollClick}>{label}</button>;
    }

    if (courseAccess.status === "checking") {
      return (
        <div className="ve-access-checking" role="status" aria-label="Checking course access">
          <span className="api-content-loader" aria-hidden="true" />
        </div>
      );
    }

    if (courseAccess.status === "enrolled") {
      return (
        <div className="ve-course-access-state" role="status">
          <strong>✓ Purchased · Lifetime access</strong>
          <span>
            {courseAccess.remainingMinutes > 0
              ? `Remaining course duration: ${formatRemainingDuration(courseAccess.remainingMinutes)}`
              : "Course completed"}
          </span>
        </div>
      );
    }

    if (courseAccess.status === "error") {
      return (
        <div className="ve-course-access-state" role="alert">
          <span>Unable to verify course access.</span>
          <button
            className="ve-access-retry-btn"
            onClick={() => setAccessCheckVersion((version) => version + 1)}
          >
            Retry
          </button>
          <button className={className} onClick={handleEnrollClick}>{label}</button>
        </div>
      );
    }

    return <button className={className} onClick={handleEnrollClick}>{label}</button>;
  };

  return (
    <div className="ve-page-wrapper">
      
      <div className="ve-breadcrumb-strip">
        <div className="ve-max-width">
          <span className="ve-bc-link" onClick={() => (window.location.hash = "#home")}>
            Home
          </span>
          <span className="ve-bc-sep">➔</span>
          <span className="ve-bc-link" onClick={() => (window.location.hash = "#courses")}>
            Courses
          </span>
          <span className="ve-bc-sep">➔</span>
          <span className="ve-bc-current">{courseTitle}</span>
        </div>
      </div>

      
      <section className="ve-hero-banner-section">
        <div className="ve-max-width ve-hero-grid">
          <div className="ve-hero-left">
            <div className="ve-badge-pill">
              <span className="ve-badge-dot">●</span> 🎬 Pro Editing Masterclass
            </div>

            <h1 className="ve-hero-title">{courseTitle}</h1>

            <p className="ve-hero-desc">
              {courseDescription}
            </p>

            <div className="ve-hero-stats-row">
              <div className="ve-rating-box">
                ★ 4.8 <span className="ve-gold-stars">★★★★★</span> <span>(Course Reviews)</span>
              </div>
              <div className="ve-stat-divider">|</div>
              <div className="ve-learner-count">👥 {courseModuleCount} Modules • {courseLectureCount} Lessons</div>
            </div>

            <div className="ve-hero-cta-group">
              {renderEnrollmentAction(
                "ve-btn-enroll-hero",
                <>Join Now • {displayCoursePrice} {displayCourseOriginalPrice !== "₹0" && <del>{displayCourseOriginalPrice}</del>}</>,
              )}
              <button
                className="ve-btn-demo-trigger"
                onClick={() => {
                  const target = document.getElementById("ve-checkout-target");
                  if (target) target.scrollIntoView({ behavior: "smooth" });
                }}
              >
                View Syllabus ➔
              </button>
            </div>
          </div>

          
          <div className="ve-hero-right">
            <div className="ve-trailer-card" ref={heroTrailerRef}>
              <CustomYouTubePlayer
                videoUrl={databaseCourse?.videoUrl || "https://www.youtube.com/embed/-34KeSMV0SI"}
                title={`${courseTitle} Trailer`}
                autoPlayWhenVisible
                mutedAutoplay
              />
              {/* <div className="ve-trailer-duration-badge">⏱️ {courseDuration} Course Preview</div> */}
            </div>
          </div>
        </div>
      </section>

      {/* 2. EDIT VIDEO LIKE */}
      <section className="ve-top-creators-section">
        <div className="ve-max-width">
          <div className="ve-creators-reference-grid">
            {topCreators.map((creator) => (
              <div key={creator.id} className="ve-creator-showcase-card">
                <div className="ve-creator-photo-wrap">
                  <img
                    src={creator.image}
                    alt={creator.name}
                    className="ve-creator-main-photo"
                  />
                </div>
                <div className="ve-creator-caption-box">
                  <span className="ve-creator-subtext">Edit Video Like:</span>
                  <h3 className="ve-creator-headline">{creator.name}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY SHOWCASE */}
      <section className="ve-gallery-section">
        <div className="ve-max-width">
          <div className="ve-section-split-header">
            <div>
              <span className="ve-kicker">REAL-WORLD PORTFOLIO PROJECTS</span>
              <h2>What You'll Learn to Edit</h2>
              <p>Practice with raw downloadable 4K project files from real YouTube & commercial shoots.</p>
            </div>
          </div>

          <div className="ve-gallery-grid">
            {galleryProjects.map((project) => (
              <div key={project.id} className="ve-gallery-card" onClick={() => setActiveGalleryVideo(project)}>
                <div className="ve-gallery-thumb">
                  <img src={project.thumbnail} alt={project.title} />
                  <span className="ve-gallery-badge">{project.category}</span>
                  <div className="ve-gallery-hover-overlay">
                    <span className="ve-gallery-play-btn">▶ Watch Project</span>
                  </div>
                  <span className="ve-gallery-duration">{project.duration}</span>
                </div>

                <div className="ve-gallery-body">
                  <h4>{project.title}</h4>
                  <div className="ve-gallery-meta">
                    <span>🔥 {project.views}</span>
                    <span>📁 Project Files Included</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      
          <section className="ve-max-width ve-flex-bonus-section">
        <div className="ve-flex-bonus-header">
          <span className="ve-flex-kicker">FREE BONUSES INCLUDED</span>
          <h3 className="ve-flex-bonus-title">
            Unlock Free Resources Worth <span className="ve-text-gradient">{bonusValue}</span>
          </h3>
          <p className="ve-flex-bonus-subtitle">
            Register for the Masterclass or unlock each professional asset pack individually for FREE!
          </p>
        </div>

        
        <div className="ve-flex-bonus-row">
          {bonusItems.map((bonus) => (
            <div key={bonus.id} className="ve-flex-bonus-card">
              
              <div className="ve-book-3d-stage">
                <div
                  className="ve-bonus-book-mockup"
                  style={{
                    backgroundColor: bonus.themeColor || bonus.color || "#1e293b",
                    boxShadow: `0 14px 28px -6px ${(bonus.themeColor || bonus.color || "#1e293b")}55`,
                  }}
                >
                  <span className="ve-book-page ve-page-under" />
                  <span className="ve-book-page ve-page-middle" />
                  <span className="ve-book-page ve-page-top" />

                  <div
                    className="ve-book-front-cover"
                    style={{ backgroundColor: bonus.themeColor || bonus.color || "#1e293b" }}
                  >
                    <div className="ve-book-spine" />
                    <div className="ve-book-icon-wrap">{bonus.icon || "📦"}</div>
                    <span className="ve-book-subtext">{bonus.label || bonus.title}</span>
                  </div>
                </div>
              </div>

              
              <div className="ve-flex-card-body">
                <h4 className="ve-flex-card-title">{bonus.title}</h4>
                <div className="ve-flex-price-pill">
                  <del className="ve-flex-old-price">{bonus.originalPrice || bonus.originalPriceText || "₹0"}</del>
                  <span className="ve-flex-free-tag">100% FREE</span>
                </div>
              </div>

              
              <button
                type="button"
                className="ve-flex-unlock-btn"
                onClick={() => handleOpenBonusModal(bonus)}
              >
                <span>🔒</span> Unlock Now
              </button>
            </div>
          ))}
        </div>

        
        {activeBonusModal && (
          <div className="cd-bonus-modal-curtain">
            <div
              className="cd-bonus-modal-backdrop"
              onClick={handleCloseBonusModal}
            />
            <div className="cd-bonus-modal-card">
              <button
                className="cd-bonus-modal-close"
                onClick={handleCloseBonusModal}
              >
                ✕
              </button>

              {!isUnlocked ? (
                <div className="cd-bonus-form-content">
                  <div className="cd-bonus-modal-icon-header">
                    <div
                      className="cd-bonus-modal-badge-icon"
                      style={{ backgroundColor: activeBonusModal.themeColor || activeBonusModal.color || "#1e293b" }}
                    >
                      {activeBonusModal.icon}
                    </div>
                    <h3>Unlock Free Resource</h3>
                    <p>
                      Enter your details to get instant access to{" "}
                      <strong>{activeBonusModal.title}</strong> ({activeBonusModal.originalPrice} Value).
                    </p>
                  </div>

                  <form
                    onSubmit={handleBonusFormSubmit}
                    className="cd-bonus-lead-form"
                  >
                    <div className="cd-form-group">
                      <label>Full Name</label>
                      <input
                        type="text"
                        required
                        placeholder="Enter your name"
                        value={leadForm.name}
                        onChange={(e) =>
                          setLeadForm({ ...leadForm, name: e.target.value })
                        }
                      />
                    </div>

                    <div className="cd-form-group">
                      <label>Email Address</label>
                      <input
                        type="email"
                        required
                        placeholder="name@example.com"
                        value={leadForm.email}
                        onChange={(e) =>
                          setLeadForm({ ...leadForm, email: e.target.value })
                        }
                      />
                    </div>

                    <div className="cd-form-group">
                      <label>WhatsApp / Phone Number</label>
                      <input
                        type="tel"
                        required
                        placeholder="+91 98765 43210"
                        value={leadForm.phone}
                        onChange={(e) =>
                          setLeadForm({ ...leadForm, phone: e.target.value })
                        }
                      />
                    </div>

                    <button type="submit" className="cd-bonus-submit-btn">
                      🔓 Submit & Unlock Resource
                    </button>
                  </form>
                </div>
              ) : (
                <div className="cd-bonus-success-content">
                  <div className="cd-success-icon-bubble">🎉</div>
                  <h3>Resource Unlocked!</h3>
                  <p>
                    Hey <strong>{leadForm.name}</strong>, your access link for{" "}
                    <strong>{activeBonusModal.title}</strong> is ready.
                  </p>

                  <a
                    href="#download"
                    className="cd-bonus-download-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      alert(`Downloading ${activeBonusModal.title}...`);
                    }}
                  >
                    📥 Download Resource Now
                  </a>

                  <span className="cd-success-footnote">
                    A backup link has also been sent to <strong>{leadForm.email}</strong>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      
      <section className="ve-max-width ve-main-content-grid" id="ve-checkout-target">
        {/* LEFT COLUMN */}
        <div className="ve-content-left">
          <div className="ve-tabs-nav-bar">
            {["overview", "course content", "reviews"].map((tab) => (
              <button
                key={tab}
                className={`ve-tab-btn ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === "overview" && (
            <div className="ve-tab-pane">
              <h3>Course Overview</h3>
              <p className="ve-overview-p">
                {courseDescription || "An overview has not been added for this course yet."}
              </p>

              {includedCourseFeatures.length > 0 && (
                <div className="ve-skills-grid">
                  {includedCourseFeatures.map((feature, index) => (
                    <div className="ve-skill-box" key={`${feature}-${index}`}>
                      {feature}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {(activeTab === "overview" || activeTab === "course content") && (
            <div className="ve-tab-pane">
              <div className="ve-curriculum-header">
                <h3>Detailed Course Curriculum</h3>
                <span>
                  {courseModuleCount} Modules • {courseLectureCount} Lessons
                  {courseDuration ? ` • ${courseDuration} Total Watch Time` : ""}
                </span>
              </div>

              <div className="ve-accordion-stack">
                {editingCurriculum.map((module) => (
                  <div key={module.id} className="ve-accordion-node">
                    <div className="ve-accordion-head" onClick={() => toggleModule(module.id)}>
                      <div className="ve-head-left">
                        <span className="ve-arrow-icon">{expandedModules[module.id] ? "▼" : "▶"}</span>
                        <h4>{module.title}</h4>
                      </div>
                      <span className="ve-head-meta">{module.meta}</span>
                    </div>

                    {expandedModules[module.id] && (
                      <div className="ve-accordion-body">
                        {module.lectures.map((lec, idx) => (
                          <div key={idx} className="ve-lecture-row">
                            <div className="ve-lec-title">🎥 {lec.title}</div>
                            <span className="ve-lec-time">{lec.time}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {editingCurriculum.length === 0 && (
                  <p className="ve-overview-p">
                    Course content has not been added yet.
                  </p>
                )}
              </div>
            </div>
          )}

          
              <div className="ve-tab-pane ve-cert-box">
            <div className="ve-cert-left">
              <span className="ve-kicker">OFFICIAL CREDENTIAL</span>
              <h3>Get Industry-Recognized Certification</h3>
              <p>
                Showcase your accredited skills to creators, agencies, and international clients on LinkedIn & Upwork.
              </p>
              <ul className="ve-cert-bullet-points">
                <li>✓ Verifiable digital certificate ID</li>
                <li>✓ Shareable on LinkedIn & portfolio sites</li>
                <li>✓ Awarded after completing the final challenge project</li>
              </ul>
            </div>
          
            
            <div className="ve-cert-right">
              <div className="ve-cert-png-wrapper">
                <img
                  src={customCertificateImg}
                  alt="Official Course Completion Certificate"
                  className="ve-cert-custom-png"
                  loading="lazy"
                />
                <div className="ve-cert-floating-badge">★ Verified Credential ★</div>
              </div>
            </div>
          </div>

          
          <div className="ve-tab-pane cd-reviews-override-block">
            <div className="section-flex-header">
              <h2>
                Our Students <span>Feedback.</span>
              </h2>
              <a href="#stories" className="global-redirect-link results-all-desktop">
                View All Stories ➔
              </a>
            </div>

            <div className="horizontal-carousel-viewport">
              {studentFeedbacks.map((data) => (
                <ResultCard
                  key={data.id}
                  resultData={data}
                  isPlaying={activePlayingId === data.id}
                  onPlay={() => setActivePlayingId(data.id)}
                  onPause={() => setActivePlayingId(null)}
                />
              ))}
            </div>

            <div className="results-all-mobile-wrapper">
              <a href="#stories" className="global-redirect-link results-all-mobile">
                View All Stories ➔
              </a>
            </div>
          </div>

         
          <div className="ve-tab-pane">
            <h3>Frequently Asked Questions</h3>
            {faqsList.length > 0 ? (
              <div className="ve-faqs-dual-flex">
                {[faqsList.filter((_, index) => index % 2 === 0), faqsList.filter((_, index) => index % 2 === 1)]
                  .map((column, columnIndex) => (
                    <div className="ve-faq-column" key={columnIndex}>
                      {column.map((faq, index) => {
                        const faqIndex = columnIndex + index * 2;
                        return (
                          <div className="ve-faq-item" key={`${faq.question}-${faqIndex}`}>
                            <div className="ve-faq-head" onClick={() => toggleFaq(faqIndex)}>
                              <h4>{faq.question}</h4>
                              <span>{openFaq === faqIndex ? "−" : "+"}</span>
                            </div>
                            {openFaq === faqIndex && (
                              <p className="ve-faq-answer">{faq.answer}</p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
              </div>
            ) : (
              <p className="ve-overview-p">
                FAQs have not been added for this course yet.
              </p>
            )}
          </div>

         
          <div className="ve-tab-pane ve-about-brief-pane">
            <span className="ve-kicker">WHO WE ARE</span>
            <h3>About Creators Dost</h3>
            <p className="ve-overview-p">
              Creators Dost is India's premier creator education platform founded by Graduate FF and experienced digital mentors. Having produced over 2 Billion+ views across 5+ channels, we bridge the gap between amateur hobby editing and high-ticket professional creator careers through battle-tested, practical workflows.
            </p>
          </div>
        </div>

        
        <div className="ve-content-right">
          <div className="ve-sticky-card-box">
            <div className="ve-checkout-card">
              <div className="ve-deal-ribbon">🔥 60% OFF Launch Discount • Ending Soon!</div>

              
              <div className="ve-checkout-timer-row">
                <div className="ve-timer-unit">
                  <h4>{String(timeLeft.days).padStart(2, "0")}</h4>
                  <p>Days</p>
                </div>
                <div className="ve-timer-unit">
                  <h4>{String(timeLeft.hours).padStart(2, "0")}</h4>
                  <p>Hrs</p>
                </div>
                <div className="ve-timer-unit">
                  <h4>{String(timeLeft.minutes).padStart(2, "0")}</h4>
                  <p>Mins</p>
                </div>
                <div className="ve-timer-unit">
                  <h4>{String(timeLeft.seconds).padStart(2, "0")}</h4>
                  <p>Secs</p>
                </div>
              </div>

              <div className="ve-price-matrix-row">
                <div>
                  <span className="ve-offer-lbl">Special Price</span>
                  <h2 className="ve-big-price">{displayCoursePrice}</h2>
                </div>
                <div className="ve-strike-price-box">
                  <p>Regular Price</p>
                  <span>{displayCourseOriginalPrice}</span>
                </div>
              </div>

              {renderEnrollmentAction(
                "ve-btn-enroll-primary",
                "Join Now",
              )}

              <div className="ve-guarantee-strip">🛡️ 30-Day Unconditional Money-Back Guarantee</div>

              {includedCourseFeatures.length > 0 && (
                <>
                  <h3>Included With This Course</h3>
                  <ul className="ve-included-checklist">
                    {includedCourseFeatures.map((feature, index) => (
                      <li key={`${feature}-${index}`}>✓ <strong>{feature}</strong></li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            <div className="ve-why-card">
              <h4>Why Editors Choose Creators Dost</h4>
              <p>Learn the actual production habits behind multi-million view videos—not just where buttons are located in the software.</p>
            </div>
          </div>
        </div>
      </section>

      
      {activeGalleryVideo && (
        <div className="ve-gallery-modal-curtain">
          <div className="ve-modal-backdrop" onClick={() => setActiveGalleryVideo(null)} />
          <div className="ve-gallery-modal-body">
            <div className="ve-gallery-modal-header">
              <div>
                <h3>{activeGalleryVideo.title}</h3>
                <span>{activeGalleryVideo.category}</span>
              </div>
              <button className="ve-close-btn" onClick={() => setActiveGalleryVideo(null)}>
                ✕ Close
              </button>
            </div>

            <div className="ve-gallery-iframe-wrapper">
              <CustomYouTubePlayer
                videoUrl={activeGalleryVideo.videoUrl}
                title={activeGalleryVideo.title}
                autoplay
              />
            </div>
          </div>
        </div>
      )}

      {purchaseDialogOpen && (
        <div className="ve-purchase-dialog-curtain">
          <div
            className="ve-purchase-dialog-backdrop"
            onClick={() => purchaseStatus !== "processing" && setPurchaseDialogOpen(false)}
          />
          <div className="ve-purchase-dialog" role="dialog" aria-modal="true" aria-labelledby="ve-purchase-dialog-title">
            <button
              className="ve-purchase-dialog-close"
              type="button"
              onClick={() => setPurchaseDialogOpen(false)}
              disabled={purchaseStatus === "processing"}
              aria-label="Close purchase dialog"
            >
              ✕
            </button>

            {purchaseStatus === "error" ? (
              <div className="ve-purchase-dialog-state">
                <span className="ve-purchase-dialog-icon error">!</span>
                <h3 id="ve-purchase-dialog-title">Purchase Failed</h3>
                <p>{purchaseError}</p>
                <button
                  className="ve-purchase-dialog-primary"
                  type="button"
                  onClick={() => {
                    setPurchaseDialogOpen(false);
                    handleEnrollClick();
                  }}
                >
                  Try Again
                </button>
              </div>
            ) : (
              <div className="ve-purchase-dialog-state">
                <span className="ve-purchase-dialog-icon">₹</span>
                <h3 id="ve-purchase-dialog-title">Opening Secure Checkout</h3>
                <p>Connecting to PhonePe for {courseTitle}...</p>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={`ve-mobile-floating-bar${isHeroTrailerVisible ? " ve-mobile-floating-bar-trailer-visible" : ""}`}>
        <div className="ve-mobile-bar-price">
          <span className="ve-bar-deal-tag">Limited Offer</span>
          <div className="ve-bar-price-row">
            <h4>{displayCoursePrice}</h4>
            <span className="ve-bar-strike">{displayCourseOriginalPrice}</span>
          </div>
        </div>
        {renderEnrollmentAction("ve-mobile-bar-btn", "Join Now ➔")}
      </div>
    </div>
  );
}